import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { deliveryApi, farmApi, animalFormOptionsApi } from '../services/api';
import { handleApiError, showError, showSuccess } from '../utils/handleApiError';
import { Farm, Animal, MasterDataValue } from '../types';
import ConfirmModal from '../components/ConfirmModal';
import MasterDataCombobox from '../components/MasterDataCombobox';

// Step types
type WizardStep = 'details' | 'earTags' | 'commonData' | 'review' | 'edit';

// Form data types
interface DeliveryDetails {
  farm_id: string;
  delivery_date: string;
  seller: string;
  transport_company: string;
  total_transport_cost: string;
  invoice_number: string;
  contract_number: string;
  origin_country: string;
  notes: string;
}

interface CommonAnimalData {
  birth_date: string;
  gender: 'him' | 'nosteny';
  breed: string;
  mother_ear_tag: string;
  origin_farm_code: string;
  invoice_number: string;
  contract_number: string;
  purchase_weight: string;
  price_per_kg: string;
}

interface GeneratedAnimal extends CommonAnimalData {
  ear_tag: string;
  id?: number;
}

const BulkDelivery: React.FC = () => {
  const navigate = useNavigate();

  // State management
  const [currentStep, setCurrentStep] = useState<WizardStep>('details');
  const [farms, setFarms] = useState<Farm[]>([]);
  const [breeds, setBreeds] = useState<MasterDataValue[]>([]);
  const [originCountries, setOriginCountries] = useState<MasterDataValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [deliveryId, setDeliveryId] = useState<number | null>(null);
  const [currentAnimalIndex, setCurrentAnimalIndex] = useState(0);

  // Form data state
  const [deliveryDetails, setDeliveryDetails] = useState<DeliveryDetails>({
    farm_id: '',
    delivery_date: new Date().toISOString().split('T')[0],
    seller: '',
    transport_company: '',
    total_transport_cost: '',
    invoice_number: '',
    contract_number: '',
    origin_country: '',
    notes: ''
  });

  const [earTagsInput, setEarTagsInput] = useState('');
  const [earTags, setEarTags] = useState<string[]>([]);
  const [earTagsError, setEarTagsError] = useState('');

  const [commonData, setCommonData] = useState<CommonAnimalData>({
    birth_date: '',
    gender: 'him',
    breed: '',
    mother_ear_tag: '',
    origin_farm_code: '',
    invoice_number: '',
    contract_number: '',
    purchase_weight: '',
    price_per_kg: ''
  });

  const [generatedAnimals, setGeneratedAnimals] = useState<GeneratedAnimal[]>([]);
  const [editedAnimals, setEditedAnimals] = useState<Map<number, GeneratedAnimal>>(new Map());
  const [showFinalizeConfirm, setShowFinalizeConfirm] = useState(false);

  // Load farms and breeds on mount
  useEffect(() => {
    loadFarms();
    loadBreeds();
    loadCountries();
  }, []);

  const loadFarms = async () => {
    try {
      const farmsData = await farmApi.getAll();
      setFarms(farmsData);
    } catch (error) {
      console.error('Error loading farms:', error);
      handleApiError(error, 'Hiba a telepek betöltésekor');
    }
  };

  const loadBreeds = async () => {
    try {
      const breedsData = await animalFormOptionsApi.getBreeds();
      setBreeds(breedsData || []);
    } catch (error) {
      console.error('Error loading breeds:', error);
      handleApiError(error, 'Hiba a fajták betöltésekor');
    }
  };

  const loadCountries = async () => {
    try {
      const data = await animalFormOptionsApi.getCountries();
      setOriginCountries(data || []);
    } catch (error) {
      console.error('Error loading countries:', error);
      handleApiError(error, 'Hiba az országok betöltésekor');
    }
  };

  // Validate ear tags input
  const validateEarTags = () => {
    const tags = earTagsInput
      .split('\n')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    if (tags.length === 0) {
      setEarTagsError('Legalább egy fülszámot meg kell adni');
      return false;
    }

    // Check for duplicates
    const uniqueTags = new Set(tags);
    if (uniqueTags.size !== tags.length) {
      setEarTagsError('Duplikált fülszámok találhatók');
      return false;
    }

    setEarTags(tags);
    setEarTagsError('');
    return true;
  };

  // Calculate total price per animal
  const calculateTotalPrice = (weight: string, pricePerKg: string): number => {
    const w = parseFloat(weight) || 0;
    const p = parseFloat(pricePerKg) || 0;
    return w * p;
  };

  // Calculate total estimated cost
  const calculateTotalEstimatedCost = (): number => {
    const animalCost = generatedAnimals.reduce((sum, animal) => {
      return sum + calculateTotalPrice(animal.purchase_weight, animal.price_per_kg);
    }, 0);

    const transportCost = parseFloat(deliveryDetails.total_transport_cost) || 0;
    return animalCost + transportCost;
  };

  // Generate animals from ear tags and common data
  const generateAnimals = () => {
    const animals: GeneratedAnimal[] = earTags.map(earTag => ({
      ear_tag: earTag,
      ...commonData
    }));
    setGeneratedAnimals(animals);
  };

  // Step navigation handlers
  const handleNextStep = async () => {
    if (currentStep === 'details') {
      if (!deliveryDetails.farm_id || !deliveryDetails.delivery_date || !deliveryDetails.seller) {
        showError('Kérjük töltse ki az összes kötelező mezőt');
        return;
      }
      setCurrentStep('earTags');
    } else if (currentStep === 'earTags') {
      if (!validateEarTags()) {
        return;
      }
      // Pre-fill invoice/contract from delivery details on first visit
      setCommonData(prev => ({
        ...prev,
        invoice_number: prev.invoice_number || deliveryDetails.invoice_number,
        contract_number: prev.contract_number || deliveryDetails.contract_number,
      }));
      setCurrentStep('commonData');
    } else if (currentStep === 'commonData') {
      if (!commonData.birth_date || !commonData.breed || !commonData.purchase_weight || !commonData.price_per_kg) {
        showError('Kérjük töltse ki az összes kötelező mezőt');
        return;
      }
      generateAnimals();
      setCurrentStep('review');
    } else if (currentStep === 'review') {
      await createDeliveryAndAnimals();
    }
  };

  const handlePreviousStep = () => {
    if (currentStep === 'earTags') {
      setCurrentStep('details');
    } else if (currentStep === 'commonData') {
      setCurrentStep('earTags');
    } else if (currentStep === 'review') {
      setCurrentStep('commonData');
    } else if (currentStep === 'edit') {
      setCurrentStep('review');
    }
  };

  // Create delivery and bulk create animals
  const createDeliveryAndAnimals = async () => {
    setLoading(true);
    try {
      // Step 1: Create draft delivery
      const deliveryData = {
        farm_id: parseInt(deliveryDetails.farm_id),
        type: 'incoming' as const,
        delivery_date: deliveryDetails.delivery_date,
        seller: deliveryDetails.seller,
        transport_company: deliveryDetails.transport_company || undefined,
        total_transport_cost: deliveryDetails.total_transport_cost ? parseFloat(deliveryDetails.total_transport_cost) : undefined,
        invoice_number: deliveryDetails.invoice_number || undefined,
        contract_number: deliveryDetails.contract_number || undefined,
        notes: deliveryDetails.notes || undefined,
        status: 'draft' as const
      };

      const delivery = await deliveryApi.create(deliveryData);
      setDeliveryId(delivery.id);

      // Step 2: Bulk create animals
      const bulkData = {
        ear_tags: earTags,
        common_data: {
          birth_date: commonData.birth_date,
          gender: commonData.gender,
          breed: commonData.breed,
          mother_ear_tag: commonData.mother_ear_tag || undefined,
          origin_farm_code: commonData.origin_farm_code || undefined,
          origin_country: deliveryDetails.origin_country || undefined,
          seller: deliveryDetails.seller,
          invoice_number: commonData.invoice_number || undefined,
          contract_number: commonData.contract_number || undefined,
          purchase_weight: parseFloat(commonData.purchase_weight),
          price_per_kg: parseFloat(commonData.price_per_kg)
        }
      };

      const response = await deliveryApi.bulkCreateAnimals(delivery.id, bulkData);

      // Update generated animals with IDs from response
      if (response.animals) {
        const animalsWithIds = generatedAnimals.map((animal, index) => ({
          ...animal,
          id: response.animals[index]?.id
        }));
        setGeneratedAnimals(animalsWithIds);
      }

      showSuccess('Szállítás és állatok sikeresen létrehozva');
      setCurrentStep('edit');
    } catch (error: any) {
      console.error('Error creating delivery and animals:', error);
      handleApiError(error, 'Hiba a szállítás és állatok létrehozásakor');
    } finally {
      setLoading(false);
    }
  };

  // Update individual animal
  const handleSaveAnimal = async (index: number) => {
    if (!deliveryId || !generatedAnimals[index].id) return;

    setLoading(true);
    try {
      const animal = editedAnimals.get(index) || generatedAnimals[index];
      const animalData = {
        birth_date: animal.birth_date,
        gender: animal.gender,
        breed: animal.breed,
        mother_ear_tag: animal.mother_ear_tag || undefined,
        origin_farm_code: animal.origin_farm_code || undefined,
        invoice_number: animal.invoice_number || undefined,
        contract_number: animal.contract_number || undefined,
        purchase_weight: parseFloat(animal.purchase_weight),
        price_per_kg: parseFloat(animal.price_per_kg),
        purchase_price: calculateTotalPrice(animal.purchase_weight, animal.price_per_kg)
      };

      await deliveryApi.updateDeliveryAnimal(deliveryId, generatedAnimals[index].id!, animalData);

      // Update the generated animals array with saved changes
      const updatedAnimals = [...generatedAnimals];
      updatedAnimals[index] = { ...animal };
      setGeneratedAnimals(updatedAnimals);

      // Clear from edited map
      const newEditedAnimals = new Map(editedAnimals);
      newEditedAnimals.delete(index);
      setEditedAnimals(newEditedAnimals);

      showSuccess('Állat adatai sikeresen mentve');
    } catch (error: any) {
      console.error('Error updating animal:', error);
      handleApiError(error, 'Hiba az állat adatainak mentésekor');
    } finally {
      setLoading(false);
    }
  };

  // Finalize delivery
  const handleFinalizeDelivery = () => {
    if (!deliveryId) return;

    if (editedAnimals.size > 0) {
      setShowFinalizeConfirm(true);
      return;
    }

    doFinalizeDelivery();
  };

  const doFinalizeDelivery = async () => {
    if (!deliveryId) return;

    setLoading(true);
    try {
      await deliveryApi.finalizeDelivery(deliveryId);
      showSuccess('Szállítás sikeresen véglegesítve!');
      navigate('/deliveries');
    } catch (error: any) {
      console.error('Error finalizing delivery:', error);
      handleApiError(error, 'Hiba a szállítás véglegesítésekor');
    } finally {
      setShowFinalizeConfirm(false);
      setLoading(false);
    }
  };

  // Update edited animal data
  const updateAnimalField = (index: number, field: keyof CommonAnimalData, value: string) => {
    const currentAnimal = editedAnimals.get(index) || generatedAnimals[index];
    const updatedAnimal = { ...currentAnimal, [field]: value };
    const newEditedAnimals = new Map(editedAnimals);
    newEditedAnimals.set(index, updatedAnimal);
    setEditedAnimals(newEditedAnimals);
  };

  // Get current animal data (edited or original)
  const getCurrentAnimalData = (index: number): GeneratedAnimal => {
    return editedAnimals.get(index) || generatedAnimals[index];
  };

  // Render step indicator
  const renderStepIndicator = () => {
    const steps = [
      { key: 'details', label: 'Szállítási adatok', number: 1 },
      { key: 'earTags', label: 'Fülszámok', number: 2 },
      { key: 'commonData', label: 'Közös adatok', number: 3 },
      { key: 'review', label: 'Áttekintés', number: 4 },
      { key: 'edit', label: 'Szerkesztés', number: 5 }
    ];

    const currentStepIndex = steps.findIndex(s => s.key === currentStep);

    return (
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center">
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold
                  ${index <= currentStepIndex
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-300 text-gray-600'}
                `}>
                  {step.number}
                </div>
                <div className={`
                  mt-2 text-xs text-center
                  ${index <= currentStepIndex ? 'text-green-600 font-medium' : 'text-gray-500'}
                `}>
                  {step.label}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`
                  flex-1 h-1 mx-2
                  ${index < currentStepIndex ? 'bg-green-600' : 'bg-gray-300'}
                `} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'details':
        return (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Szállítási részletek</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telep <span className="text-red-500">*</span>
                </label>
                <select
                  value={deliveryDetails.farm_id}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, farm_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Válassz telepet...</option>
                  {farms.map(farm => (
                    <option key={farm.id} value={farm.id}>{farm.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Szállítási dátum <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={deliveryDetails.delivery_date}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, delivery_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Eladó neve <span className="text-red-500">*</span>
                </label>
                <MasterDataCombobox
                  categoryName="sellers"
                  value={deliveryDetails.seller}
                  onChange={(val) => setDeliveryDetails({ ...deliveryDetails, seller: val })}
                  placeholder="Eladó neve vagy cégnév"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Szállítócég
                </label>
                <MasterDataCombobox
                  categoryName="transport_companies"
                  value={deliveryDetails.transport_company}
                  onChange={(val) => setDeliveryDetails({ ...deliveryDetails, transport_company: val })}
                  placeholder="Szállítócég neve"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Összes szállítási költség (HUF)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={deliveryDetails.total_transport_cost}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, total_transport_cost: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Számlaszám
                </label>
                <input
                  type="text"
                  value={deliveryDetails.invoice_number}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, invoice_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Számla száma"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Szerződésszám
                </label>
                <input
                  type="text"
                  value={deliveryDetails.contract_number}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, contract_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Szerződés száma"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Származási ország
                </label>
                <select
                  value={deliveryDetails.origin_country}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, origin_country: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Válassz országot...</option>
                  {originCountries.map(country => (
                    <option key={country.id} value={country.value}>
                      {country.display_value || country.value}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Megjegyzések
                </label>
                <textarea
                  value={deliveryDetails.notes}
                  onChange={(e) => setDeliveryDetails({ ...deliveryDetails, notes: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="További megjegyzések..."
                />
              </div>
            </div>
          </div>
        );

      case 'earTags':
        return (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Fülszámok megadása</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fülszámok (soronként egy) <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={earTagsInput}
                  onChange={(e) => setEarTagsInput(e.target.value)}
                  rows={15}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500 font-mono"
                  placeholder="HU123456789&#10;HU987654321&#10;HU555444333"
                />
                {earTagsError && (
                  <p className="mt-2 text-sm text-red-600">{earTagsError}</p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm text-blue-800">
                    {earTagsInput.split('\n').filter(t => t.trim().length > 0).length} fülszám megadva
                  </span>
                </div>
              </div>

              {earTags.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Előnézet:</h3>
                  <div className="bg-gray-50 rounded-md p-4 max-h-60 overflow-y-auto">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {earTags.map((tag, index) => (
                        <div key={index} className="bg-white border border-gray-200 rounded px-3 py-2 text-sm font-mono">
                          {tag}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'commonData':
        return (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Közös állat adatok</h2>
            <p className="text-sm text-gray-600 mb-6">
              Ezeket az adatokat minden állat megkapja. A következő lépésben módosíthatja őket egyedileg.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Születési dátum <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={commonData.birth_date}
                  onChange={(e) => setCommonData({ ...commonData, birth_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nem <span className="text-red-500">*</span>
                </label>
                <select
                  value={commonData.gender}
                  onChange={(e) => setCommonData({ ...commonData, gender: e.target.value as 'him' | 'nosteny' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="him">Hím</option>
                  <option value="nosteny">Nőstény</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fajta <span className="text-red-500">*</span>
                </label>
                <select
                  value={commonData.breed}
                  onChange={(e) => setCommonData({ ...commonData, breed: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  required
                >
                  <option value="">Válassz fajtát...</option>
                  {breeds.map(breed => (
                    <option key={breed.id} value={breed.value}>
                      {breed.display_value || breed.value}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Anyaállat fülszáma
                </label>
                <input
                  type="text"
                  value={commonData.mother_ear_tag}
                  onChange={(e) => setCommonData({ ...commonData, mother_ear_tag: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="pl. HU123456789"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Származási tenyészet
                </label>
                <input
                  type="text"
                  value={commonData.origin_farm_code}
                  onChange={(e) => setCommonData({ ...commonData, origin_farm_code: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Tenyészet kódja"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Számlaszám
                </label>
                <input
                  type="text"
                  value={commonData.invoice_number}
                  onChange={(e) => setCommonData({ ...commonData, invoice_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Számla száma"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Szerződésszám
                </label>
                <input
                  type="text"
                  value={commonData.contract_number}
                  onChange={(e) => setCommonData({ ...commonData, contract_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="Szerződés száma"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vásárlási súly (kg) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={commonData.purchase_weight}
                  onChange={(e) => setCommonData({ ...commonData, purchase_weight: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kilogrammonkénti ár (HUF) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={commonData.price_per_kg}
                  onChange={(e) => setCommonData({ ...commonData, price_per_kg: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="md:col-span-2 bg-green-50 border border-green-200 rounded-md p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-green-800">
                    Számított ár állatonként:
                  </span>
                  <span className="text-lg font-bold text-green-900">
                    {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(
                      calculateTotalPrice(commonData.purchase_weight, commonData.price_per_kg)
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'review':
        return (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Összegzés</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Szállítási adatok</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Telep:</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {farms.find(f => f.id === parseInt(deliveryDetails.farm_id))?.name}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Dátum:</dt>
                      <dd className="text-sm font-medium text-gray-900">{deliveryDetails.delivery_date}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Eladó:</dt>
                      <dd className="text-sm font-medium text-gray-900">{deliveryDetails.seller}</dd>
                    </div>
                    {deliveryDetails.transport_company && (
                      <div className="flex justify-between">
                        <dt className="text-sm text-gray-600">Szállítócég:</dt>
                        <dd className="text-sm font-medium text-gray-900">{deliveryDetails.transport_company}</dd>
                      </div>
                    )}
                  </dl>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Állatok</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Állatok száma:</dt>
                      <dd className="text-sm font-medium text-gray-900">{generatedAnimals.length}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Fajta:</dt>
                      <dd className="text-sm font-medium text-gray-900">{commonData.breed}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Nem:</dt>
                      <dd className="text-sm font-medium text-gray-900">
                        {commonData.gender === 'him' ? 'Hím' : 'Nőstény'}
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-600">Súly/állat:</dt>
                      <dd className="text-sm font-medium text-gray-900">{commonData.purchase_weight} kg</dd>
                    </div>
                  </dl>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">Költségek</h3>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Állatok összesen:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(
                        generatedAnimals.reduce((sum, a) => sum + calculateTotalPrice(a.purchase_weight, a.price_per_kg), 0)
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-600">Szállítási költség:</dt>
                    <dd className="text-sm font-medium text-gray-900">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(
                        parseFloat(deliveryDetails.total_transport_cost) || 0
                      )}
                    </dd>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <dt className="text-base font-semibold text-gray-900">Összköltség:</dt>
                    <dd className="text-base font-bold text-green-600">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(
                        calculateTotalEstimatedCost()
                      )}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800">
                  A <strong>Állatok létrehozása</strong> gombra kattintva a rendszer létrehozza a szállítást és az összes állatot.
                  A következő lépésben lehetősége lesz egyenként szerkeszteni az állatok adatait.
                </div>
              </div>
            </div>
          </div>
        );

      case 'edit':
        if (generatedAnimals.length === 0) return null;

        const currentAnimal = getCurrentAnimalData(currentAnimalIndex);

        return (
          <div className="space-y-6">
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Állat {currentAnimalIndex + 1} / {generatedAnimals.length}
                </h2>
                <div className="text-sm text-gray-600">
                  Fülszám: <span className="font-mono font-bold">{currentAnimal.ear_tag}</span>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Születési dátum <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={currentAnimal.birth_date}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'birth_date', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nem <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentAnimal.gender}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'gender', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="him">Hím</option>
                    <option value="nosteny">Nőstény</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fajta <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={currentAnimal.breed}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'breed', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="">Válassz fajtát...</option>
                    {breeds.map(breed => (
                      <option key={breed.id} value={breed.value}>
                        {breed.display_value || breed.value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Anyaállat fülszáma
                  </label>
                  <input
                    type="text"
                    value={currentAnimal.mother_ear_tag}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'mother_ear_tag', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Származási tenyészet
                  </label>
                  <input
                    type="text"
                    value={currentAnimal.origin_farm_code}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'origin_farm_code', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Számlaszám
                  </label>
                  <input
                    type="text"
                    value={currentAnimal.invoice_number}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'invoice_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Szerződésszám
                  </label>
                  <input
                    type="text"
                    value={currentAnimal.contract_number}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'contract_number', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vásárlási súly (kg) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentAnimal.purchase_weight}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'purchase_weight', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kilogrammonkénti ár (HUF) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={currentAnimal.price_per_kg}
                    onChange={(e) => updateAnimalField(currentAnimalIndex, 'price_per_kg', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
                  />
                </div>

                <div className="md:col-span-2 bg-green-50 border border-green-200 rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-800">
                      Számított ár:
                    </span>
                    <span className="text-lg font-bold text-green-900">
                      {new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(
                        calculateTotalPrice(currentAnimal.purchase_weight, currentAnimal.price_per_kg)
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t">
                <div className="flex items-center justify-between">
                  {editedAnimals.has(currentAnimalIndex) && (
                    <div className="flex items-center text-amber-600 text-sm">
                      <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      Mentetlen változások
                    </div>
                  )}
                  <button
                    onClick={() => handleSaveAnimal(currentAnimalIndex)}
                    disabled={loading || !editedAnimals.has(currentAnimalIndex)}
                    className="ml-auto bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Változások mentése
                  </button>
                </div>
              </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex items-center justify-between">
                <button
                  onClick={() => setCurrentAnimalIndex(Math.max(0, currentAnimalIndex - 1))}
                  disabled={currentAnimalIndex === 0}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Előző
                </button>

                <div className="text-center">
                  <div className="text-sm text-gray-600">Haladás</div>
                  <div className="mt-1">
                    <div className="w-64 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${((currentAnimalIndex + 1) / generatedAnimals.length) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>

                {currentAnimalIndex < generatedAnimals.length - 1 ? (
                  <button
                    onClick={() => setCurrentAnimalIndex(Math.min(generatedAnimals.length - 1, currentAnimalIndex + 1))}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Következő
                    <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                ) : (
                  <button
                    onClick={handleFinalizeDelivery}
                    disabled={loading}
                    className="flex items-center px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Szállítás véglegesítése
                  </button>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tömeges állat beérkezés</h1>
            <p className="mt-2 text-gray-600">
              Több állat egyidejű rögzítése egyszerűen
            </p>
          </div>
          <button
            onClick={() => navigate('/deliveries')}
            className="flex items-center px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Mégse
          </button>
        </div>
      </div>

      {renderStepIndicator()}
      {renderStepContent()}

      {currentStep !== 'edit' && (
        <div className="mt-8 flex justify-between">
          <button
            onClick={handlePreviousStep}
            disabled={currentStep === 'details'}
            className="flex items-center px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Vissza
          </button>

          <button
            onClick={handleNextStep}
            disabled={loading}
            className="flex items-center px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Feldolgozás...
              </>
            ) : currentStep === 'review' ? (
              <>
                Állatok létrehozása
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            ) : (
              <>
                Tovább
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </>
            )}
          </button>
        </div>
      )}
      <ConfirmModal
        isOpen={showFinalizeConfirm}
        onConfirm={doFinalizeDelivery}
        onCancel={() => setShowFinalizeConfirm(false)}
        title="Véglegesítés"
        message="Van mentetlen változtatás. Biztosan véglegesíti a szállítást?"
        confirmLabel="Véglegesítés"
        variant="warning"
      />
    </div>
  );
};

export default BulkDelivery;
